import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import StatusBadge from "@/components/app/StatusBadge";
import RecordConversation from "@/components/app/RecordConversation";
import RecordStickyNotes from "@/components/app/RecordStickyNotes";
import CourierSelect from "@/components/app/CourierSelect";
import { REQ } from "@/constants/testIds";
import { toast } from "sonner";
import { Search, CheckCircle2, XCircle, Truck, FileText } from "lucide-react";
import { RequestDcDialog } from "@/components/requests/RequestDcDialog";
import { DispatchWhatsAppButton, DispatchWhatsAppDialog } from "@/components/dispatch/DispatchWhatsAppDialog";
import { DcWorkspace } from "@/pages/dispatch/DcWorkspace";
import { Textarea } from "@/components/ui/textarea";
import OneBefore from "@/components/app/OneBefore";
import { useAuth } from "@/context/AuthContext";

const STATUSES = ["all", "pending_approval", "approved", "in_progress", "delivered", "closed", "rejected", "cancelled"];

const slaText = (d) => {
  if (!d.sla_due) return d.sla_status || "—";
  const due = new Date(d.sla_due);
  const h = Math.round((due - Date.now()) / 36e5);
  const when = due.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" });
  const left = ["completed", "closed", "cancelled", "delivered"].includes(String(d.status).toLowerCase()) ? "" : h < 0 ? ` · overdue by ${Math.abs(h) >= 48 ? `${Math.round(-h / 24)} days` : `${-h} h`}` : ` · ${h >= 48 ? `${Math.round(h / 24)} days` : `${h} h`} left`;
  const done = ["completed", "closed", "cancelled", "delivered"].includes(String(d.status).toLowerCase());
  const state = !done && h < 0 ? "Breached" : String(d.sla_status || "—").replace(/_/g, " ").toLowerCase().replace(/^./, (c) => c.toUpperCase());
  return `${state} · due ${when} IST${left}`;
};

export default function Requests() {
  const navigate = useNavigate();
  const { user } = useAuth?.() || { user: {} };
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [subsidiary, setSubsidiary] = useState("all");
  const [employee, setEmployee] = useState("all");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [shipOpen, setShipOpen] = useState(false);
  const [shipForm, setShipForm] = useState({ courier: "", awb: "", contact_phone: "" });
  const [dcInfo, setDcInfo] = useState(null);      // { active, challans } for the open request
  const [dcOpen, setDcOpen] = useState(false);     // Create DC dialog
  const [dcWork, setDcWork] = useState(null);      // DcWorkspace dcId
  const loadDc = async (id) => { try { const { data } = await api.get(`/delivery-challans/request/${id}/prefill`); setDcInfo(data); return data; } catch { setDcInfo(null); return null; } };
  const [approveOpen, setApproveOpen] = useState(false);
  const [approveComment, setApproveComment] = useState("");
  const [rejectComment, setRejectComment] = useState("");

  const fetchRows = () => {
    setLoading(true);
    api
      .get("/requests/", {
        params: {
          q: q || undefined,
          status: status !== "all" ? status : undefined,
          company_id: subsidiary !== "all" ? subsidiary : undefined,
          employee_id: employee !== "all" ? employee : undefined,
          limit: 50,
        },
      })
      .then(({ data }) => {
        setRows(data.items || []);
        setTotal(data.total || 0);
      })
      .finally(() => setLoading(false));
  };
  useEffect(fetchRows, [q, status, subsidiary, employee]);

  // Build filter option lists from the currently loaded rows.
  const subsidiaryOptions = Array.from(
    new Map(rows.filter((r) => r.company_id && r.company_name)
      .map((r) => [r.company_id, r.company_name])).entries())
    .sort((a, b) => (a[1] || "").localeCompare(b[1] || ""));
  const employeeOptions = Array.from(
    new Map(rows.filter((r) => r.employee_id && r.employee_name)
      .map((r) => [r.employee_id, r.employee_name])).entries())
    .sort((a, b) => (a[1] || "").localeCompare(b[1] || ""));

  const openDetail = async (id) => {
    const { data } = await api.get(`/requests/${id}`);
    setDetail(data);
    if (["approved", "in_progress"].includes(data.status)) loadDc(id); else setDcInfo(null);
  };
  useEffect(() => {  // deep link from the DC desk / board: /requests?open=<request>&dc=<challan> — read-only lookups, opens the request's own DC editor
    const sp = new URLSearchParams(window.location.search);
    const rid = sp.get("open"), dcid = sp.get("dc");
    if (!rid) return;
    navigate("/requests", { replace: true });
    (async () => {
      try { await openDetail(rid); } catch { toast.error("The request for this challan was not found in your company scope"); return; }
      if (!dcid) return;
      try { const { data } = await api.get(`/delivery-challans/request/${rid}/prefill`); if ((data.challans || []).some((c) => c.id === dcid)) setDcWork(dcid); else toast.error("That Delivery Challan is not part of this request"); }
      catch { toast.error("Could not load this request's Delivery Challan"); }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const activeDc = dcInfo?.active || null;
  const dcGenerated = !!activeDc && !["DRAFT", "ADDRESS_VERIFIED"].includes(activeDc.status);

  const approve = async (action) => {
    if (!detail) return;
    try {
      await api.post(`/requests/${detail.id}/approve`, { action, comment: action === "approve" ? approveComment : rejectComment });
      toast.success(`Request ${action === "approve" ? "approved" : "rejected"}`);
      setApproveOpen(false);
      setApproveComment("");
      setRejectComment("");
      await openDetail(detail.id);
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const SHIP_NEXT = { packed: [["dispatched", "Mark dispatched"], ["cancelled", "Cancel"]], dispatched: [["in_transit", "In transit"], ["delivered", "Delivered"]], in_transit: [["out_for_delivery", "Out for delivery"], ["delivered", "Delivered"]], out_for_delivery: [["delivered", "Delivered"]], delivered: [["closed", "Close"]] };
  const [shipBusy, setShipBusy] = useState("");
  const [waShipment, setWaShipment] = useState(null);
  const setShipStatus = async (s, status) => {
    if (status === "cancelled" && !window.confirm(`Cancel shipment ${s.shipment_code}?`)) return;
    setShipBusy(s.id);
    try {
      const { data } = await api.post(`/shipments/${s.id}/status`, { status });
      toast.success(`${s.shipment_code} → ${status.replace(/_/g, " ")}${["dispatched", "in_transit", "delivered"].includes(status) ? " · customer dashboard updated" : ""}`);
      await openDetail(detail.id); fetchRows();
      if (status === "dispatched" && data?.dispatch_notice?.state !== "ACCEPTED") setWaShipment(s.id);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.error === "preflight_blocked") toast.error(`${d.message} ${(d.failed_checks || []).map((c) => c.name).join(" · ")}`, { duration: 8000 });
      else toast.error(typeof d === "string" ? d : d?.message || "Could not update shipment");
    } finally { setShipBusy(""); }
  };
  const activeShipment = (detail?.shipments || []).some((s) => !["cancelled", "closed"].includes(s.status));
  const createShipment = async () => {
    if (!detail) return;
    try {
      await api.post(`/shipments`, {
        request_id: detail.id,
        master_company_id: detail.master_company_id,
        company_id: detail.company_id,
        employee_id: detail.employee_id,
        asset_ids: detail.asset_ids,
        courier: shipForm.courier || activeDc?.dispatch?.courier || activeDc?.dispatch_through,
        awb: shipForm.awb || activeDc?.dispatch?.awb,
        dc_id: activeDc?.id,
        dc_code: activeDc?.code,
        address: detail.address,
        contact_name: detail.employee?.name,
        contact_phone: shipForm.contact_phone || detail.employee?.phone,
      });
      await api.post(`/requests/${detail.id}/status`, { status: "in_progress", sub_status: "shipment_created" });
      toast.success("Shipment created & queued");
      setShipOpen(false);
      setShipForm({ courier: "", awb: "", contact_phone: "" });
      await openDetail(detail.id);
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-4 max-w-[1600px]">
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">
            Lifecycle · Requests
          </div>
          <h1 className="text-2xl font-heading font-semibold text-slate-900 tracking-tight">Requests</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Every service request across your organization.
          </p>
        </div>
        <RecordConversation contextType="general" contextLabel="Requests" />
      </div>

      {/* ONE Before · replace — nudge users to reuse eligible stock. */}
      <div data-testid="requests-onebefore-replace">
        <OneBefore action="replace" masterCompanyId={user?.master_company_id} />
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-3 flex flex-col sm:flex-row gap-2 sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search by request code or notes…"
            className="pl-9 h-9"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-9 w-full sm:w-44" data-testid="req-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            {STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                <span className="capitalize">{s.replace(/_/g, " ")}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={subsidiary} onValueChange={setSubsidiary}>
          <SelectTrigger className="h-9 w-full sm:w-56" data-testid="req-filter-subsidiary">
            <SelectValue placeholder="All subsidiaries" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All subsidiaries</SelectItem>
            {subsidiaryOptions.map(([id, name]) => (
              <SelectItem key={id} value={id}>{name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={employee} onValueChange={setEmployee}>
          <SelectTrigger className="h-9 w-full sm:w-56" data-testid="req-filter-employee">
            <SelectValue placeholder="All employees" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All employees</SelectItem>
            {employeeOptions.map(([id, name]) => (
              <SelectItem key={id} value={id}>{name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={() => navigate("/deploy")}>New request</Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <Table data-testid={REQ.table}>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-[10px] uppercase tracking-wider text-slate-500">Request</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider text-slate-500">Subsidiary</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider text-slate-500">Employee</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider text-slate-500">Device</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider text-slate-500">Type</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider text-slate-500">Priority</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider text-slate-500">Status</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider text-slate-500">SLA</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider text-slate-500">Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={9} className="text-center py-8 text-sm text-slate-500">Loading…</TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="text-center py-8 text-sm text-slate-500">No requests found.</TableCell></TableRow>
            ) : rows.map((r) => (
              <TableRow
                key={r.id}
                data-testid={REQ.row(r.id)}
                className="cursor-pointer hover:bg-slate-50"
                onClick={() => openDetail(r.id)}
              >
                <TableCell className="font-mono text-xs text-slate-700">{r.request_code}</TableCell>
                <TableCell className="text-sm text-slate-800" data-testid={`req-subsidiary-${r.id}`}>
                  {r.company_name || r.master_company_name || <span className="text-slate-400">—</span>}
                </TableCell>
                <TableCell className="text-sm text-slate-800" data-testid={`req-employee-${r.id}`}>
                  {r.employee_name ? (
                    <div className="min-w-0">
                      <div className="truncate max-w-[180px]">{r.employee_name}</div>
                      {r.employee_email && (
                        <div className="text-[10px] text-slate-400 font-mono truncate max-w-[180px]">{r.employee_email}</div>
                      )}
                    </div>
                  ) : <span className="text-slate-400">—</span>}
                </TableCell>
                <TableCell className="text-sm text-slate-700" data-testid={`req-device-${r.id}`}>
                  {r.assets_summary?.length ? (
                    <div className="space-y-0.5">
                      {r.assets_summary.slice(0, 2).map((a, i) => (
                        <div key={i} className="text-[11px]">
                          <span className="text-slate-800">{[a.brand, a.model].filter(Boolean).join(" ") || a.category || "—"}</span>
                          {(a.serial || a.asset_tag) && (
                            <span className="text-[10px] text-slate-400 font-mono ml-1">
                              · {a.serial || a.asset_tag}
                            </span>
                          )}
                        </div>
                      ))}
                      {r.assets_summary.length > 2 && (
                        <div className="text-[10px] text-slate-400">+{r.assets_summary.length - 2} more</div>
                      )}
                    </div>
                  ) : (
                    <span className="text-slate-400">{r.asset_ids?.length ? `${r.asset_ids.length} asset(s)` : "—"}</span>
                  )}
                </TableCell>
                <TableCell className="text-sm text-slate-800 capitalize">{r.request_type?.replace(/_/g, " ")}</TableCell>
                <TableCell><StatusBadge value={r.priority} /></TableCell>
                <TableCell><StatusBadge value={r.status} /></TableCell>
                <TableCell><StatusBadge value={r.sla_status} /></TableCell>
                <TableCell className="text-xs text-slate-500">{new Date(r.created_at).toLocaleDateString()}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {waShipment && <DispatchWhatsAppDialog kind="shipment" id={waShipment} onClose={() => setWaShipment(null)} onSent={() => detail && openDetail(detail.id)} />}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl" onOpenAutoFocus={(e) => { e.preventDefault(); document.getElementById("request-detail-title")?.focus(); }}>
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle id="request-detail-title" tabIndex={-1} className="font-heading text-lg outline-none" data-testid="request-detail-title">
                  {detail.request_code}
                  <span className="ml-2 inline-block align-middle"><StatusBadge value={detail.status} /></span>
                </DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Info label="Type" value={detail.request_type?.replace(/_/g, " ")} />
                <Info label="Priority" value={detail.priority} />
                <Info label="Company" value={detail.company?.name} />
                <Info label="Employee" value={detail.employee?.name} />
                <Info label="Country" value={detail.country} />
                <Info label="SLA" value={slaText(detail)} plain />
                <div className="col-span-2">
                  <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">Assets</div>
                  <div className="mt-1 space-y-1">
                    {detail.assets?.map((a) => (
                      <div key={a.id} className="text-xs bg-slate-50 border border-slate-100 rounded px-2 py-1">
                        <span className="font-mono">{a.asset_tag}</span> · {a.brand} {a.model} · <span className="font-mono">{a.serial}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="col-span-2">
                  <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">Delivery Address</div>
                  <div className="text-sm text-slate-800 mt-1">{detail.address || "—"}</div>
                </div>
                {activeDc && (
                  <div className="col-span-2" data-testid="req-dc-status">
                    <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">Delivery Challan</div>
                    <button type="button" onClick={() => setDcWork(activeDc.id)} className="mt-1 inline-flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-[12px] hover:border-slate-400">
                      <FileText className="h-3.5 w-3.5 text-slate-500" /><span className="font-mono font-semibold">{activeDc.code || "Draft"}</span>
                      <span className="rounded bg-slate-900 px-1.5 py-0.5 text-[10px] font-medium text-white">{activeDc.status.replace(/_/g, " ")}</span>
                      <span className="text-slate-500">{activeDc.items?.length} line(s){activeDc.dispatch?.courier ? ` · ${activeDc.dispatch.courier}` : ""}{activeDc.dispatch?.awb ? ` · ${activeDc.dispatch.awb}` : ""}</span>
                    </button>
                  </div>
                )}

                {detail.approvals?.length > 0 && (
                  <div className="col-span-2">
                    <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">Approvals</div>
                    <ul className="text-xs mt-1 space-y-1">
                      {detail.approvals.map((a) => (
                        <li key={a.id} className="bg-slate-50 border border-slate-100 rounded px-2 py-1">
                          {a.user_email} · {a.action} · {new Date(a.timestamp).toLocaleString()}
                          {a.comment ? ` — ${a.comment}` : ""}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {detail.shipments?.length > 0 && (
                  <div className="col-span-2">
                    <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">Shipments</div>
                    <ul className="text-xs mt-1 space-y-1">
                      {detail.shipments.map((s) => (
                        <li key={s.id} className="bg-slate-50 border border-slate-100 rounded px-2 py-1 flex flex-wrap items-center gap-2" data-testid={`req-shipment-${s.id}`}>
                          <span>{s.shipment_code} · {s.courier || "-"} {s.awb ? `· AWB ${s.awb}` : ""} ·</span> <StatusBadge value={s.status} />
                          <span className="ml-auto flex flex-wrap gap-1">
                            {["dispatched", "in_transit", "out_for_delivery"].includes(s.status) && (
                              <DispatchWhatsAppButton kind="shipment" id={s.id} label={s.dispatch_notice?.state === "ACCEPTED" ? "WhatsApp sent ✓" : "Send WhatsApp dispatch update"} testid={`req-shipment-wa-${s.id}`} onSent={() => openDetail(detail.id)} />
                            )}
                            {(SHIP_NEXT[s.status] || []).map(([st, label]) => (
                              <Button key={st} size="sm" variant={st === "cancelled" ? "ghost" : "outline"} className={`h-6 px-2 text-[11px] ${st === "cancelled" ? "text-rose-700" : ""}`}
                                disabled={shipBusy === s.id} onClick={() => setShipStatus(s, st)} data-testid={`req-shipment-${st}-${s.id}`}>{label}</Button>
                            ))}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="col-span-2 mt-2">
                  <RecordStickyNotes
                    contextType="order"
                    contextId={detail.id}
                    masterCompanyId={detail.master_company_id}
                    title="Notes on this request"
                  />
                </div>
              </div>
              <DialogFooter className="flex-wrap gap-2">
                {detail.status === "pending_approval" && (
                  <>
                    <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
                      <DialogTrigger asChild>
                        <Button data-testid={REQ.approveBtn} className="bg-emerald-700 hover:bg-emerald-800 text-white">
                          <CheckCircle2 className="h-4 w-4 mr-1.5" /> Approve
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader><DialogTitle>Approve request</DialogTitle></DialogHeader>
                        <Textarea placeholder="Comment (optional)" value={approveComment} onChange={(e) => setApproveComment(e.target.value)} />
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setApproveOpen(false)}>Cancel</Button>
                          <Button className="bg-slate-900" onClick={() => approve("approve")}>Confirm approve</Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                    <Button data-testid={REQ.rejectBtn} variant="outline" onClick={() => approve("reject")}>
                      <XCircle className="h-4 w-4 mr-1.5" /> Reject
                    </Button>
                  </>
                )}
                {["approved", "in_progress"].includes(detail.status) && !dcGenerated && (
                  <Button className="bg-slate-900 hover:bg-slate-800 text-white" onClick={() => (activeDc ? setDcWork(activeDc.id) : setDcOpen(true))} data-testid={activeDc ? "req-open-dc" : "req-create-dc"}>
                    <FileText className="h-4 w-4 mr-1.5" /> {activeDc ? `Continue DC · ${activeDc.status.replace(/_/g, " ").toLowerCase()}` : "Create DC"}
                  </Button>
                )}
                {["approved", "in_progress"].includes(detail.status) && dcGenerated && (
                  <Button variant="outline" onClick={() => setDcWork(activeDc.id)} data-testid="req-view-dc"><FileText className="h-4 w-4 mr-1.5" /> {activeDc.code}</Button>
                )}
                {["approved", "in_progress"].includes(detail.status) && dcGenerated && !activeShipment && (
                  <Dialog open={shipOpen} onOpenChange={(o) => { setShipOpen(o); if (o) setShipForm((f) => ({ ...f, courier: f.courier || activeDc?.dispatch?.courier || activeDc?.dispatch_through || "", awb: f.awb || activeDc?.dispatch?.awb || "" })); }}>
                    <DialogTrigger asChild>
                      <Button className="bg-slate-900 hover:bg-slate-800 text-white">
                        <Truck className="h-4 w-4 mr-1.5" /> Create shipment
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader><DialogTitle>Create Forward Shipment</DialogTitle></DialogHeader>
                      <div className="space-y-3">
                        <div className="rounded-md bg-emerald-50 px-2 py-1.5 text-[11px] text-emerald-900" data-testid="ship-dc-ref">Against Delivery Challan <b className="font-mono">{activeDc?.code}</b> · {activeDc?.items?.length} line(s) · courier/AWB pre-filled from the DC</div>
                        <div>
                          <label className="text-xs">Courier</label>
                          <CourierSelect
                            value={shipForm.courier}
                            onChange={(v) => setShipForm({ ...shipForm, courier: v })}
                            testId="ship-courier-select"
                          />
                        </div>
                        <div>
                          <label className="text-xs">AWB (optional)</label>
                          <Input value={shipForm.awb} onChange={(e) => setShipForm({ ...shipForm, awb: e.target.value })} />
                        </div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setShipOpen(false)}>Cancel</Button>
                        <Button className="bg-slate-900" onClick={createShipment}>Create shipment</Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                )}
              </DialogFooter>
              {dcOpen && <RequestDcDialog request={detail} onClose={() => setDcOpen(false)} onCreated={async (dc) => { setDcOpen(false); await loadDc(detail.id); setDcWork(dc.id); }} />}
              {dcWork && <DcWorkspace dcId={dcWork} onClose={async () => { setDcWork(null); await loadDc(detail.id); await openDetail(detail.id); fetchRows(); }} onChanged={() => loadDc(detail.id)} />}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Info({ label, value, plain = false }) {
  return (
    <div>
      <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">{label}</div>
      <div className={`text-sm text-slate-800 mt-0.5 ${plain ? "" : "capitalize"}`}>{value || "—"}</div>
    </div>
  );
}
